import React, { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect } from 'react';
import { Table, Pagination, Form, Spinner } from 'react-bootstrap';
import { ArrowUp, ArrowDown, ArrowUpDown, ChevronUp, ChevronDown } from 'lucide-react';
import moment from 'moment';
import { debounce } from 'lodash';

const PinnedSectionDivider = ({ colSpan, interactive = false, expanded = false, onToggle }) => {
  if (!interactive) {
    return (
      <tr aria-hidden="true" className="orders-table__pinned-divider">
        <td colSpan={colSpan} style={{padding: 0, border: 'none', lineHeight: 0 }}>
          <div style={{ height: '2px', backgroundColor: '#F5A623' }} />
        </td>
      </tr>
    );
  }

  const handleToggle = e => {
    e.stopPropagation();
    onToggle();
  };

  return (
    <tr
      className="orders-table__pinned-divider orders-table__pinned-divider--interactive"
      role="button"
      tabIndex={0}
      title={expanded ? 'Collapse pinned orders' : 'Expand pinned orders'}
      aria-expanded={expanded}
      aria-label={expanded ? 'Collapse pinned orders' : 'Expand pinned orders'}
      onClick={onToggle}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggle();
        }
      }}
    >
      <td colSpan={colSpan} style={{ padding: 0, border: 'none', lineHeight: 0 }}>
        <div className="orders-table__pinned-divider-bar" onClick={handleToggle} role="presentation">
          <span className="orders-table__pinned-divider-icon" onClick={handleToggle} role="presentation">
            {expanded ? <ChevronUp /> : <ChevronDown />}
          </span>
        </div>
      </td>
    </tr>
  );
};

const DataTable = ({
  // Data props
  data = [],
  columns = [],
  rowKey = 'id',

  // Sorting props
  sortable = true,
  defaultSort = { key: null, direction: 'asc' },
  onSortChange,

  // Pagination props
  pagination = true,
  pageSize = 10,
  currentPage = 1,
  onPageChange,

  /** When true (or when totalCount is a number), data is one server page — no client slice; use totalCount / totalPages for the footer. */
  serverPagination = false,
  totalCount,
  totalPages: totalPagesOverride,

  /** When true, skip client-side sorting — data is already sorted by the server. */
  serverSort = false,

  // Interaction props
  onRowClick,
  onRowDoubleClick,

  // Styling props
  striped = false,
  hover = true,
  bordered = false,
  responsive = false,
  className = '',

  // Loading state
  loading = false,

  // Empty state
  emptyMessage = 'No data available',

  // Custom renderers
  renderEmptyState,
  renderLoadingState,

  // UI Enhancement props
  maxHeight = '100vh',

  // Advanced features
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  onSelectAll,
  checkboxColumn = false,
  onTableScrollTopChange,
  onBottomReached,
  bottomOffset = 10,
  loadingMoreData = false,
  expandedRowKeys = [],
  renderExpandedRow,
  rowToHighlight,
  highLightRowColor = 'primary-light',
  getRowClassName,
  getRowStyle,
  showPinnedSectionDivider = false,
  isPinnedRow,
  pinnedSectionCollapsedLimit = 3,
  onRowContextMenu
}) => {
  const [sortConfig, setSortConfig] = useState(defaultSort);
  const [pinnedSectionExpanded, setPinnedSectionExpanded] = useState(false);
  const tableContainerRef = useRef(null);
  const prevPinnedCountRef = useRef(0);

  // Resizable columns state - initialized from column props
  const [columnWidths, setColumnWidths] = useState(() => {
    // Handle empty or missing columns
    if (!columns || columns.length === 0) {
      return {};
    }

    // Initialize widths from column props
    const widths = {};
    columns.forEach(col => {
      // Skip columns without keys
      if (!col?.key) return;

      if (col.width) {
        const num = typeof col.width === 'number' ? col.width : parseInt(String(col.width).replace(/[^0-9]/g, ''), 10);
        widths[col.key] = isNaN(num) || num < 50 ? 150 : Math.min(num, 5000); // clamp between 50-5000
      } else {
        widths[col.key] = 150; // default width
      }
    });
    return widths;
  });

  const [resizingState, setResizingState] = useState(null);
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const [hoveredRowKey, setHoveredRowKey] = useState(null);
  const headerRowRef = useRef(null);

  // Sync column widths when columns change (add new columns, preserve existing widths)
  useEffect(() => {
    // Handle empty columns
    if (!columns || columns.length === 0) {
      setColumnWidths({});
      return;
    }

    setColumnWidths(prev => {
      const updated = { ...prev }; // Start with existing widths
      const allColumnKeys = new Set(columns.map(col => col?.key).filter(Boolean));

      // Add widths for new columns
      columns.forEach(col => {
        // Skip columns without keys
        if (!col?.key) return;

        // If we don't have a width for this column yet
        if (!updated[col.key] || typeof updated[col.key] !== 'number' || updated[col.key] < 50) {
          // Use column prop width or default
          if (col.width) {
            const num =
              typeof col.width === 'number' ? col.width : parseInt(String(col.width).replace(/[^0-9]/g, ''), 10);
            updated[col.key] = isNaN(num) || num < 50 ? 150 : Math.min(num, 5000);
          } else {
            updated[col.key] = 150;
          }
        }
      });

      // Remove widths for columns that no longer exist
      Object.keys(updated).forEach(key => {
        if (!allColumnKeys.has(key)) {
          delete updated[key];
        }
      });

      return updated;
    });
  }, [columns]);

  // Compute a sensible min-width from provided column widths so the table
  // fills available space but scrolls when columns overflow
  const computedMinWidth = useMemo(() => {
    try {
      const base = selectable && checkboxColumn ? 50 : 0;
      const sum = (columns || []).reduce((acc, col) => {
        const width = columnWidths[col.key] || 150;
        return acc + width;
      }, base);
      return sum > 0 ? sum : '100%';
    } catch {
      return '100%';
    }
  }, [columns, selectable, checkboxColumn, columnWidths]);

  // Scroll handling
  useEffect(() => {
    const checkBottom = () => {
      if (loadingMoreData) return;
      const element = tableContainerRef.current;
      if (element) {
        const scrollTop = element.scrollTop;
        const distanceFromBottom = element.scrollHeight - scrollTop - element.clientHeight;

        if (distanceFromBottom <= bottomOffset) {
          onBottomReached && onBottomReached();
        }
        if (onTableScrollTopChange) {
          onTableScrollTopChange(scrollTop);
        }
      }
    };

    const debouncedHandleScroll = debounce(checkBottom, 100);

    const element = tableContainerRef.current;
    if (element) {
      element.addEventListener('scroll', debouncedHandleScroll);
      // Initial check in case content doesn't fill viewport
      checkBottom();
      return () => {
        element.removeEventListener('scroll', debouncedHandleScroll);
        debouncedHandleScroll.cancel();
      };
    }
  }, [bottomOffset, onBottomReached, loadingMoreData, onTableScrollTopChange]);

  // Column resize handlers
  const handleResizeStart = useCallback(
    (columnKey, initialMouseX) => {
      const initialWidth = columnWidths[columnKey] || 150;
      setResizingState({
        columnKey,
        startX: initialMouseX,
        startWidth: initialWidth
      });
    },
    [columnWidths]
  );

  const handleResizeMove = useCallback(
    e => {
      if (!resizingState) return;

      const diff = e.clientX - resizingState.startX;
      // Clamp width between 50px (min) and 5000px (max)
      const newWidth = Math.max(50, Math.min(5000, resizingState.startWidth + diff));

      setColumnWidths(prev => ({
        ...prev,
        [resizingState.columnKey]: newWidth
      }));
    },
    [resizingState]
  );

  const handleResizeEnd = useCallback(() => {
    setResizingState(null);
    setHoveredColumn(null);
  }, []);

  // Handle resize mouse events
  useEffect(() => {
    if (!resizingState) return;

    const handleMouseMove = e => {
      e.preventDefault();
      handleResizeMove(e);
    };

    const handleMouseUp = () => {
      handleResizeEnd();
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'col-resize';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }, [resizingState, handleResizeMove, handleResizeEnd]);

  // Selection handling
  const handleRowSelection = useCallback(
    (rowId, isSelected) => {
      if (!onSelectionChange) return;

      let newSelection;
      if (isSelected) {
        newSelection = [...selectedRows, rowId];
      } else {
        newSelection = selectedRows.filter(id => id !== rowId);
      }
      onSelectionChange(newSelection);
    },
    [selectedRows, onSelectionChange]
  );

  // Handle sorting
  const handleSort = useCallback(
    columnKey => {
      if (!sortable || !columnKey) return;

      let direction = 'asc';
      if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
        direction = 'desc';
      }

      const newSortConfig = { key: columnKey, direction };
      setSortConfig(newSortConfig);

      if (onSortChange) {
        onSortChange(newSortConfig);
      }
    },
    [sortConfig, sortable, onSortChange]
  );

  // Sort data
  const sortedData = useMemo(() => {
    if (serverSort || !sortConfig.key || !sortable) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      // Special handling for status column
      if (sortConfig.key === 'status') {
        const getStatusSortOrder = order => {
          return order.statusSortOrder;
        };

        const aStatusSortOrder = getStatusSortOrder(a);
        const bStatusSortOrder = getStatusSortOrder(b);

        // Compare using statusSortOrder
        const comparison = aStatusSortOrder - bStatusSortOrder;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'asc' ? -1 : 1;
      if (bValue == null) return sortConfig.direction === 'asc' ? 1 : -1;

      // Handle different data types
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        const comparison = aValue.toLowerCase().localeCompare(bValue.toLowerCase());
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle dates
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Default comparison
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, sortable, serverSort]);

  const hasServerPagination = serverPagination === true || typeof totalCount === 'number';

  // Pagination logic
  const paginatedData = useMemo(() => {
    if (!pagination) return sortedData;
    if (hasServerPagination) return sortedData;

    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return sortedData.slice(startIndex, endIndex);
  }, [sortedData, currentPage, pageSize, pagination, hasServerPagination]);

  // Pagination info
  const totalItems = hasServerPagination && typeof totalCount === 'number' ? totalCount : sortedData.length;

  const totalPages = useMemo(() => {
    if (!pagination) return 1;
    if (hasServerPagination) {
      if (typeof totalPagesOverride === 'number' && totalPagesOverride >= 1) {
        return totalPagesOverride;
      }
      if (typeof totalCount === 'number') {
        return Math.max(1, Math.ceil(totalCount / pageSize));
      }
      return Math.max(1, Math.ceil(sortedData.length / pageSize));
    }
    return Math.max(1, Math.ceil(sortedData.length / pageSize));
  }, [pagination, hasServerPagination, totalPagesOverride, totalCount, pageSize, sortedData.length]);

  const startItem = useMemo(() => {
    if (!pagination) return 1;
    if (totalItems === 0) return 0;
    return (currentPage - 1) * pageSize + 1;
  }, [pagination, totalItems, currentPage, pageSize]);

  const endItem = useMemo(() => {
    if (!pagination) return totalItems;
    if (totalItems === 0) return 0;
    return Math.min(currentPage * pageSize, totalItems);
  }, [pagination, totalItems, currentPage, pageSize]);

  // Selection logic (after paginatedData is defined)
  const isAllSelected = paginatedData.length > 0 && paginatedData.every(row => selectedRows.includes(row[rowKey]));
  const isIndeterminate = selectedRows.length > 0 && !isAllSelected;

  const handleSelectAll = useCallback(() => {
    if (!onSelectAll) return;

    const allRowIds = paginatedData.map(row => row[rowKey]);
    const isAllSelected = allRowIds.every(id => selectedRows.includes(id));

    if (isAllSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(allRowIds);
    }
  }, [paginatedData, selectedRows, onSelectAll, rowKey]);

  const colSpan = columns.length + (selectable && checkboxColumn ? 1 : 0);

  const { pinnedSection, bodyRows } = useMemo(() => {
    const rows = [];
    const pushRow = (row, index, dividerBefore = false, dividerAfter = false) => {
      rows.push({ row, index, dividerBefore, dividerAfter });
    };

    if (!showPinnedSectionDivider || typeof isPinnedRow !== 'function') {
      paginatedData.forEach((row, index) => pushRow(row, index));
      return { pinnedSection: null, bodyRows: rows };
    }

    let pinnedCount = 0;
    for (const row of paginatedData) {
      if (isPinnedRow(row)) pinnedCount++;
      else break;
    }

    const isCollapsible = pinnedCount > pinnedSectionCollapsedLimit;
    const isExpanded = isCollapsible && pinnedSectionExpanded;
    const visiblePinnedCount = isExpanded ? pinnedCount : Math.min(pinnedCount, pinnedSectionCollapsedLimit);
    let pinnedIndex = 0;

    for (let index = 0; index < paginatedData.length; index++) {
      const row = paginatedData[index];
      if (!isPinnedRow(row)) {
        pushRow(row, index, !isCollapsible && index > 0 && isPinnedRow(paginatedData[index - 1]));
        continue;
      }
      if (isCollapsible && pinnedIndex >= visiblePinnedCount) {
        pinnedIndex++;
        continue;
      }
      pushRow(row, index, false, isCollapsible && pinnedIndex === visiblePinnedCount - 1);
      pinnedIndex++;
    }

    return {
      pinnedSection: { pinnedCount, isCollapsible, visiblePinnedCount, isExpanded, useSticky: !isExpanded },
      bodyRows: rows
    };
  }, [paginatedData, showPinnedSectionDivider, isPinnedRow, pinnedSectionExpanded, pinnedSectionCollapsedLimit]);

  useEffect(() => {
    const count = pinnedSection?.pinnedCount ?? 0;
    if (prevPinnedCountRef.current !== count) {
      prevPinnedCountRef.current = count;
      setPinnedSectionExpanded(false);
    }
  }, [pinnedSection?.pinnedCount]);

  // Update pinned sticky layout
  useLayoutEffect(() => {
    const container = tableContainerRef.current;
    if (!container || loading) return;

    const clearPinnedStickyStyles = () => {
      container.querySelectorAll('tbody tr.orders-table-row--pinned').forEach(rowEl => {
        rowEl.style.removeProperty('--pinned-sticky-top');
        rowEl.style.removeProperty('--pinned-sticky-z-index');
      });

      const divider = container.querySelector('.orders-table__pinned-divider');
      if (divider) {
        divider.style.removeProperty('--pinned-divider-sticky-top');
        divider.style.removeProperty('--pinned-divider-sticky-z-index');
      }
    };

    if (!pinnedSection?.useSticky) {
      clearPinnedStickyStyles();
      return;
    }

    const updatePinnedStickyLayout = () => {
      const headerHeight = headerRowRef.current?.offsetHeight ?? 0;
      const pinnedRows = [...container.querySelectorAll('tbody tr.orders-table-row--pinned')];
      let offset = headerHeight;

      pinnedRows.forEach((rowEl, index) => {
        if (index > 0) {
          offset -= 1;
        }
        rowEl.style.setProperty('--pinned-sticky-top', `${Math.round(offset)}px`);
        rowEl.style.setProperty('--pinned-sticky-z-index', String(5 + index));
        offset += rowEl.getBoundingClientRect().height;
      });

      const divider = container.querySelector('.orders-table__pinned-divider');
      if (divider) {
        divider.style.setProperty('--pinned-divider-sticky-top', `${Math.round(offset - 1)}px`);
        divider.style.setProperty('--pinned-divider-sticky-z-index', String(5 + pinnedRows.length));
      }
    };

    updatePinnedStickyLayout();

    const resizeObserver = new ResizeObserver(updatePinnedStickyLayout);
    if (headerRowRef.current) {
      resizeObserver.observe(headerRowRef.current);
    }
    container.querySelectorAll('tbody tr.orders-table-row--pinned').forEach(rowEl => {
      resizeObserver.observe(rowEl);
    });
    const divider = container.querySelector('.orders-table__pinned-divider');
    if (divider) resizeObserver.observe(divider);

    return () => resizeObserver.disconnect();
  }, [paginatedData, loading, expandedRowKeys, pinnedSection?.useSticky, pinnedSection?.visiblePinnedCount]);

  // Render sort icon
  const renderSortIcon = columnKey => {
    if (!sortable) return null;

    if (sortConfig.key === columnKey) {
      return sortConfig.direction === 'asc' ? (
        <ArrowUp className="ms-1" size={16} color="#f9d33a" />
      ) : (
        <ArrowDown className="ms-1" size={16} color="#f9d33a" />
      );
    }

    return <ArrowUpDown className="ms-1" size={16} color="#6c757d" />;
  };

  // Render cell content
  const renderCellContent = (row, column) => {
    if (column.cellRenderer) {
      return column.cellRenderer(row);
    }

    if (column.render) {
      return column.render(row[column.key], row);
    }

    const value = column.valueSelector ? column.valueSelector(row) : row[column.key];

    switch (column.type) {
      case 'date':
        return value ? moment(value).format(column.dateFormat || 'MMM DD, YYYY') : '';
      case 'currency':
        return value !== null
          ? new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: column.currency || 'USD'
            }).format(value)
          : '';
      case 'url':
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          ''
        );
      case 'email':
        return value ? <a href={`mailto:${value}`}>{value}</a> : '';
      case 'phone':
        return value ? <a href={`tel:${value}`}>{value}</a> : '';
      case 'checkbox':
        return (
          <Form.Check
            type="checkbox"
            checked={!!value}
            onChange={e => {
              e.stopPropagation();
              if (column.onCellClick) {
                column.onCellClick(row);
              }
            }}
          />
        );
      default:
        return value || '';
    }
  };

  // Handle row click
  const handleRowClick = (row, event) => {
    const selection = window.getSelection();
    if (selection.toString().length > 0) return;

    if (onRowClick) {
      onRowClick(row, event);
    }
  };

  // Handle row double click
  const handleRowDoubleClick = (row, event) => {
    const selection = window.getSelection();
    if (selection.toString().length > 0) return;

    if (onRowDoubleClick) {
      onRowDoubleClick(row, event);
    }
  };

  const handleRowContextMenu = (row, event) => {
    if (onRowContextMenu) {
      event.preventDefault();
      onRowContextMenu(row, event);
    }
  };

  // Render pagination
  const renderPagination = () => {
    if (!pagination || totalPages <= 1) return null;

    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    items.push(
      <Pagination.Prev
        key="prev"
        disabled={currentPage === 1}
        onClick={() => onPageChange && onPageChange(currentPage - 1)}
        >
        <span className="text-dark">&lt;</span>
      </Pagination.Prev>
    );

    // First page
    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => onPageChange && onPageChange(1)}>
          1
        </Pagination.Item>
      );
      if (startPage > 2) {
        items.push(<Pagination.Ellipsis key="ellipsis1" />);
      }
    }

    // Page numbers
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item key={page} active={page === currentPage} onClick={() => onPageChange && onPageChange(page)}>
          <span className="text-dark">{page}</span>
        </Pagination.Item>
      );
    }

    // Last page
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        items.push(<Pagination.Ellipsis key="ellipsis2" />);
      }
      items.push(
        <Pagination.Item key={totalPages} onClick={() => onPageChange && onPageChange(totalPages)}>
          {totalPages}
        </Pagination.Item>
      );
    }

    // Next button
    items.push(
      <Pagination.Next
        key="next"
        disabled={currentPage === totalPages}
        onClick={() => onPageChange && onPageChange(currentPage + 1)}
        >
        <span className="text-dark">&gt;</span>
      </Pagination.Next>
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="text-muted small">
          Showing {startItem} to {endItem} of {totalItems} entries
        </div>
        <Pagination className="mb-0">{items}</Pagination>
      </div>
    );
  };

  // Render loading state (skeleton)
  if (loading) {
    if (renderLoadingState) return renderLoadingState();

    const skeletonRows = 8;

    return (
      <div className={` ${className}`}>
        <div className="flex-grow-1 card " style={{ borderColor: '#dee2e6', overflow: 'hidden' }}>
          <div style={{ height: maxHeight, overflowY: 'auto', overflowX: 'auto', backgroundColor: '#fff' }}>
            <Table
              striped={striped}
              hover={hover}
              bordered={bordered}
              responsive={responsive}
              className="mb-0"
              style={{ width: '100%', minWidth: computedMinWidth, tableLayout: 'fixed', borderTop: '0' }}
            >
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr
                  style={{
                    backgroundColor: 'white',
                    color: '#495057',
                    borderBottom: '1px solid #dee2e6',
                    zIndex: 10,
                    position: 'sticky',
                    top: 0
                  }}
                >
                  {columns.map(column => (
                    <th
                      key={column.key}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        padding: '14px 16px',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textAlign: column.align || 'left',
                        width: columnWidths[column.key] || 150,
                        backgroundColor: 'white',
                        borderTop: '0'
                      }}
                    >
                      <span className="mr-2 text-muted">{column.label}</span>
                      {sortable && column.sortable !== false && renderSortIcon(column.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan={columns.length + (selectable && checkboxColumn ? 1 : 0)} className="py-3">
                    <div className="px-3">
                      <div className="shimmer shimmer-line" style={{ marginBottom: 8, width: '100%' }} />
                      <div className="shimmer shimmer-line" style={{ marginBottom: 8, width: '85%' }} />
                      <div className="shimmer shimmer-line" style={{ width: '70%' }} />
                    </div>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  // Render empty state
  if (data.length === 0) {
    return renderEmptyState ? (
      renderEmptyState()
    ) : (
      <div className={` ${className}`}>
        <div className="flex-grow-1 card " style={{ borderColor: '#dee2e6', overflow: 'hidden' }}>
          <div style={{ height: maxHeight, overflowY: 'auto', overflowX: 'auto', backgroundColor: '#fff' }}>
            <Table
              striped={striped}
              hover={hover}
              bordered={bordered}
              responsive={responsive}
              className="mb-0"
              style={{ width: '100%', minWidth: computedMinWidth, tableLayout: 'fixed', borderTop: '0' }}
            >
              <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                <tr
                  style={{
                    backgroundColor: 'white',
                    color: '#495057',
                    borderBottom: '1px solid #dee2e6',
                    zIndex: 10,
                    position: 'sticky',
                    top: 0
                  }}
                >
                  {columns.map(column => (
                    <th
                      key={column.key}
                      style={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                        padding: '14px 16px',
                        fontWeight: '600',
                        fontSize: '0.875rem',
                        textAlign: column.align || 'left',
                        width: columnWidths[column.key] || 150,
                        backgroundColor: 'white',
                        borderTop: '0'
                      }}
                    >
                      <span className="mr-2 text-muted">{column.label}</span>
                      {sortable && column.sortable !== false && renderSortIcon(column.key)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td
                    colSpan={columns.length + (selectable && checkboxColumn ? 1 : 0)}
                    style={{ padding: '1rem', textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    <h6 className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                      {emptyMessage}
                    </h6>
                  </td>
                </tr>
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={` ${className}`}>
      {/* Table Container */}
      <div className="flex-grow-1 card" style={{ borderColor: '#dee2e6', overflow: 'hidden' }}>
        <div
          ref={tableContainerRef}
          style={{ height: maxHeight, overflowY: 'auto', overflowX: 'auto', backgroundColor: '#fff' }}
        >
          <Table
            striped={striped}
            hover={hover}
            bordered={bordered}
            responsive={responsive}
            className={`mb-0${pinnedSection?.isExpanded ? ' orders-table--pinned-section-expanded' : ''}`}
            style={{ width: '100%', minWidth: computedMinWidth, tableLayout: 'fixed', borderTop: '0' }}
          >
            <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
              <tr className="text-muted" ref={headerRowRef}>
                {/* Checkbox column */}
                {selectable && checkboxColumn && (
                  <th
                    style={{
                      padding: '14px 16px',
                      width: '50px',
                      position: 'sticky',
                      top: 0,
                      zIndex: 11,
                      backgroundColor: 'white',
                      borderTop: '0'
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={isAllSelected}
                      ref={input => {
                        if (input) input.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                    />
                  </th>
                )}

                {columns.map(column => (
                  <th
                    key={column.key}
                    className={`${sortable && column.sortable !== false ? 'cursor-pointer' : ''}`}
                    onClick={e => {
                      // Don't trigger sort if clicking on resize handle
                      if (e.target.closest('.resize-handle')) return;
                      if (sortable && column.sortable !== false) {
                        handleSort(column.key);
                      }
                    }}
                    style={{
                      position: 'sticky',
                      top: 0,
                      zIndex: 11,
                      backgroundColor: 'white',
                      cursor: sortable && column.sortable !== false ? 'pointer' : 'default',
                      padding: '14px 16px',
                      fontSize: '0.875rem',
                      textAlign: column.align || 'left',
                      width: columnWidths[column.key] || 150,
                      borderTop: '0'
                    }}
                  >
                    <div
                      className={`d-flex align-items-center ${
                        column.align === 'center' ? 'justify-content-center' : ''
                      }`}
                    >
                      <span className="mr-2 text-muted">{column.label}</span>
                      {sortable && column.sortable !== false && renderSortIcon(column.key)}
                    </div>
                    {/* Resize handle */}
                    <div
                      className="position-absolute top-0 resize-handle"
                      style={{
                        right: 0,
                        top: 0,
                        bottom: 0,
                        width: '4px',
                        cursor: 'col-resize',
                        zIndex: 12,
                        userSelect: 'none',
                        pointerEvents: 'auto'
                      }}
                      onMouseDown={e => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleResizeStart(column.key, e.clientX);
                      }}
                      onMouseEnter={() => setHoveredColumn(column.key)}
                      onMouseLeave={() => {
                        if (!resizingState || resizingState.columnKey !== column.key) {
                          setHoveredColumn(null);
                        }
                      }}
                    >
                      <div
                        className="position-absolute top-0"
                        style={{
                          right: 0,
                          top: 0,
                          bottom: 0,
                          width: '4px',
                          backgroundColor: '#facc14',
                          opacity: resizingState?.columnKey === column.key || hoveredColumn === column.key ? 1 : 0,
                          transition: 'opacity 0.15s ease',
                          pointerEvents: 'none'
                        }}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paginatedData.length > 0 ? (
                bodyRows.map(({ row, index, dividerBefore, dividerAfter }) => {
                  const isSelected = selectedRows.includes(row[rowKey]);
                  const isExpanded = expandedRowKeys.includes(row[rowKey]);
                  const isHighlighted = rowToHighlight === row[rowKey];
                  const extraRowClassName = getRowClassName ? getRowClassName(row) : '';
                  const isRowHovered = hoveredRowKey === row[rowKey];
                  const extraRowStyle = getRowStyle ? getRowStyle(row, { isHovered: isRowHovered }) : null;
                  const rowBackgroundColor = extraRowStyle?.backgroundColor;

                  return (
                    <React.Fragment key={row[rowKey] || index}>
                      {dividerBefore && <PinnedSectionDivider colSpan={colSpan} />}
                      <tr
                        onClick={e => handleRowClick(row, e)}
                        onDoubleClick={e => handleRowDoubleClick(row, e)}
                        onContextMenu={e => handleRowContextMenu(row, e)}
                        onMouseEnter={() => setHoveredRowKey(row[rowKey])}
                        onMouseLeave={() => setHoveredRowKey(null)}
                        className={`${onRowClick ? 'cursor-pointer' : ''} ${isExpanded ? 'border-primary-dark' : ''} ${extraRowClassName}`}
                        style={{
                          cursor: onRowClick ? 'pointer' : 'default',
                          borderBottom: '1px solid #f1f3f4',
                          borderWidth: isExpanded ? 2 : 1,
                          border: isHighlighted ? '2px solid var(--info)' : '1px solid #f1f3f4',
                          backgroundColor: isHighlighted
                            ? `var(--${highLightRowColor})`
                            : isExpanded
                            ? 'var(--bs-primary-light)'
                            : undefined,
                          ...(extraRowStyle || {})
                        }}
                      >
                        {/* Checkbox column */}
                        {selectable && checkboxColumn && (
                          <td style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}>
                            <Form.Check
                              type="checkbox"
                              checked={isSelected}
                              onChange={e => {
                                e.stopPropagation();
                                handleRowSelection(row[rowKey], !isSelected);
                              }}
                            />
                          </td>
                        )}

                        {columns.map(column => {
                          const dynamicStyle =
                            typeof column.style === 'function' ? column.style(row[column.key], row) : column.style;

                          return (
                            <td
                              key={column.key}
                              style={{
                                padding: '12px 16px',
                                textAlign: column.align || 'left',
                                fontWeight: column.fontWeight || 'normal',
                                fontSize: column.type === 'date' ? '0.875rem' : 'inherit',
                                width: columnWidths[column.key] || 150,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'middle',
                                ...(rowBackgroundColor ? { backgroundColor: rowBackgroundColor } : {}),
                                ...dynamicStyle
                              }}
                              className={column.bg ? column.bg(row[column.key]) : ''}
                              onClick={e => {
                                if (column.onCellClick) {
                                  e.stopPropagation();
                                  column.onCellClick(row);
                                }
                              }}
                            >
                              {renderCellContent(row, column)}
                            </td>
                          );
                        })}
                      </tr>

                      {dividerAfter && (
                        <PinnedSectionDivider
                          colSpan={colSpan}
                          interactive
                          expanded={pinnedSectionExpanded}
                          onToggle={() => setPinnedSectionExpanded(prev => !prev)}
                        />
                      )}

                      {/* Expanded row */}
                      {isExpanded && renderExpandedRow && (
                        <tr className="border-primary-dark" style={{ borderWidth: 2 }}>
                          <td colSpan={colSpan} className="bg-light bg-opacity-50 p-0">
                            <div style={{ width: '95vw' }}>{renderExpandedRow(row)}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{ padding: '12px 16px', textAlign: 'center', verticalAlign: 'middle' }}
                  >
                    <h6 className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                      {emptyMessage}
                    </h6>
                  </td>
                </tr>
              )}

              {/* Loading more data */}
              {loadingMoreData && (
                <tr>
                  <td colSpan={colSpan} className="py-3" style={{ verticalAlign: 'middle' }}>
                    <div className="d-flex justify-content-center align-items-center py-1 text-muted">
                      <Spinner animation="border" size="sm" role="status" className="me-2" />
                      <span className="mx-1">Loading more...</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>
      </div>

      {/* Pagination */}
      {renderPagination()}
    </div>
  );
};

export default DataTable;
